import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { SpeakerHigh, SpeakerSlash, MagnifyingGlass, PenNib, ShareNetwork, ArrowsClockwise, CalendarCheck, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GRAD = "bg-gradient-to-r from-[#6D28D9] via-[#C026D3] to-[#F97316]";
const JOBS = [
  [MagnifyingGlass, "I find them", "Researches Australian businesses that fit, with sources for every fact."],
  [PenNib, "I write to them", "Personal emails, newsletters and posts in your voice, bold where it counts."],
  [ShareNetwork, "I show up everywhere", "LinkedIn, X, carousels and video, from one approval."],
  [ArrowsClockwise, "I never forget a follow-up", "Day 3, day 7, and replies handled. Bounces cleaned automatically."],
  [CalendarCheck, "I book the meeting", "Interested? It goes straight into your calendar."],
];

const Promo = () => {
  const v = useRef(null);
  const [muted, setMuted] = useState(true);
  return (
    <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-[0_0_80px_-20px_#C026D3]" data-testid="waitlist-video-wrap">
      <video ref={v} src="/promo/masa-promo.mp4" poster="/promo/masa-hero.jpg" autoPlay loop muted={muted} playsInline className="w-full aspect-video object-cover" data-testid="waitlist-video" />
      <button onClick={() => { setMuted(!muted); if (v.current) { v.current.muted = !muted; v.current.play(); } }} data-testid="waitlist-sound-btn"
        className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur px-3 py-1.5 text-xs text-white hover:bg-black transition-colors">
        {muted ? <SpeakerSlash /> : <SpeakerHigh />} {muted ? "Hear MaSa" : "Mute"}
      </button>
    </div>
  );
};

const Form = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "", company: "", role: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { const r = await axios.post(`${API}/public/waitlist`, f); onDone({ first: f.name.split(" ")[0], sent: r.data.email_sent !== false }); } catch (x) { setErr(x.response?.data?.detail || "Something went wrong - please try again"); }
    setBusy(false);
  };
  return (
    <form onSubmit={go} className="space-y-3" data-testid="waitlist-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input required placeholder="Your name" value={f.name} onChange={set("name")} className="bg-black/60 h-11" data-testid="waitlist-name-input" />
        <Input required type="email" placeholder="Work email" value={f.email} onChange={set("email")} className="bg-black/60 h-11" data-testid="waitlist-email-input" />
        <Input placeholder="Company" value={f.company} onChange={set("company")} className="bg-black/60 h-11" data-testid="waitlist-company-input" />
        <Input placeholder="Role (optional)" value={f.role} onChange={set("role")} className="bg-black/60 h-11" data-testid="waitlist-role-input" />
      </div>
      <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      {err && <p className="text-sm text-red-400" data-testid="waitlist-error">{err}</p>}
      <Button type="submit" disabled={busy} className={`w-full h-12 text-base font-semibold text-white ${GRAD} hover:opacity-90 transition-opacity`} data-testid="waitlist-submit-btn">
        {busy ? "Saving your spot…" : "Join the early-access waitlist"}
      </Button>
      <p className="text-xs text-zinc-500">No pricing yet, no spam. I'll email you when your spot opens. Unsubscribe any time.</p>
    </form>
  );
};

export default function Waitlist() {
  const [done, setDone] = useState(null);
  const [count, setCount] = useState(null);
  useEffect(() => { axios.get(`${API}/public/waitlist`).then((r) => setCount(r.data.count)).catch(() => {}); }, [done]);
  return (
    <div className="relative min-h-screen bg-[#050505] text-white overflow-hidden" data-testid="waitlist-page">
      <div className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#6D28D9]/25 blur-[140px]" />
      <div className="pointer-events-none absolute top-60 -right-40 w-[500px] h-[500px] rounded-full bg-[#F97316]/15 blur-[140px]" />
      <div className="relative max-w-6xl mx-auto px-6 py-10 md:py-16">
        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-12"><span className={`w-2.5 h-2.5 rounded-full ${GRAD}`} /> MaSa · an AI agent from Specia AI</div>
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-700">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">G'day, I'm MaSa.<br /><span className={`bg-clip-text text-transparent ${GRAD}`}>Your AI sales &amp; marketing team.</span></h1>
            <p className="text-base md:text-lg text-zinc-300 max-w-xl">I run the follow-ups you never get to. Give me what you're selling, a picture and a list. I'll do the research, the writing, the posting and the chasing, and you just approve.</p>
            {count > 0 && <p className="text-sm text-zinc-500" data-testid="waitlist-count">{count} Australian {count === 1 ? "business is" : "businesses are"} already on the list</p>}
          </div>
          <div className="animate-in fade-in zoom-in-95 duration-700"><Promo /></div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-16">
          {JOBS.map(([Icon, t, d], i) => (
            <div key={t} className="rounded-lg border border-white/10 bg-white/[0.03] backdrop-blur p-4 hover:border-[#C026D3]/50 transition-colors animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ animationDelay: `${i * 80}ms` }} data-testid={`waitlist-job-${i}`}>
              <Icon size={22} className="text-[#F97316]" weight="bold" />
              <div className="mt-3 text-sm font-semibold">{t}</div>
              <div className="mt-1 text-xs text-zinc-400">{d}</div>
            </div>
          ))}
        </div>
        <div className="mt-16 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="space-y-3">
            <h2 className="text-base md:text-lg font-semibold">Be one of the first teams I work for.</h2>
            <p className="text-sm text-zinc-400">I'm opening early access to a small group of Australian B2B businesses. I follow the Spam Act, honour every unsubscribe and never send a word without your approval.</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-xl p-6">
            {done !== null ? (
              <div className="flex items-start gap-3" data-testid="waitlist-success">
                <CheckCircle size={28} className="text-emerald-400" weight="fill" />
                <div><div className="font-semibold">You're on the list{done.first ? `, ${done.first}` : ""}.</div><div className="text-sm text-zinc-400 mt-1">{done.sent ? "Check your inbox. I've just sent you a hello." : "I'll be in touch as soon as your spot opens."}</div></div>
              </div>
            ) : <Form onDone={setDone} />}
          </div>
        </div>
        <p className="mt-16 text-xs text-zinc-600">© Specia AI · We only use your details to contact you about MaSa early access, and you can unsubscribe from any email.</p>
      </div>
    </div>
  );
}
